import React from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import { Hotel, PhoneOutlined } from "@mui/icons-material";

import { VM_Colors } from "../../Styles/VMStyles";
import FormFieldValues from "../../Components/Custom/FormFieldValues";
import { displayFormattedDateTime, formatAmount, isEmpty, returnDate, toTitleCase } from "../../Util/UtilHelper";
import StorageFactory from "../../Util/StorageFactory";
import { SESSION_USER_COUNTRY_DATA_KEY, STATUS_CONFIRMED } from "../../Util/VMConstants";
import MUITypography from "../../Components/MUITypography";
import VMAllocateRooms from "./VMAllocateRooms";

function VMEDEventInfo({ eventData, customer, readOnly = false, hasInvoice = false }) {
  const diningDetails = eventData?.dining_details || [];
  const isPersonCharge = eventData?.accomodation_charge_type === "person";
  const personsPayload = eventData?.accomodation_persons_data;
  const personsData = Array.isArray(personsPayload)
    ? personsPayload
    : personsPayload?.data || [];
  const personCount = personsData.length;
  const costPerPerson =
    personsData[0]?.cost ??
    (personCount > 0
      ? (personsPayload?.total_cost ?? 0) / personCount
      : 0);

  const sessionFactory = new StorageFactory('session');
  // const userID = sessionFactory.get(SESSION_USER_ID_KEY);
  const currencySymbol = sessionFactory.get(SESSION_USER_COUNTRY_DATA_KEY)?.currency_symbol || '₹';
  const isConfirmed =
    customer?.appointment_status?.toLowerCase() === STATUS_CONFIRMED.toLowerCase();

  const SectionHeader = ({ title, icon }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {icon}
      <MUITypography variant="h3" sx={{ fontWeight: 700, color: "#1E1E1E" }}>
        {title}
      </MUITypography>
    </Box>
  );

  const getPersonName = (person) =>
    toTitleCase(person.name || person.persons_name || "");
  const getPersonPhone = (person) =>
    person.phone || person.person_phone || "";
  const getPersonInitial = (person) => {
    const name = getPersonName(person);
    return name ? name.charAt(0).toUpperCase() : "?";
  };

  const renderPersonDetails = () => (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        backgroundColor: VM_Colors.white,
        border: `1px solid ${VM_Colors.light_gray}`,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          px: { xs: 2, sm: 2.5 },
          py: 1.75,
          bgcolor: VM_Colors.white1,
          borderBottom: `1px solid ${VM_Colors.light_gray}`,
        }}
      >
        <Box sx={{ minWidth: 140 }}>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 600,
              color: "#64748B",
              letterSpacing: 0.3,
              textTransform: "uppercase",
              mb: 0.35,
            }}
          >
            No. of persons
          </Typography>
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: "#0F172A" }}>
            {personCount}
          </Typography>
        </Box>
        <Box sx={{ minWidth: 160 }}>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 600,
              color: "#64748B",
              letterSpacing: 0.3,
              textTransform: "uppercase",
              mb: 0.35,
            }}
          >
            Cost per person
          </Typography>
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: VM_Colors.primary }}>
            {formatAmount(costPerPerson || 0, currencySymbol)}
          </Typography>
        </Box>
      </Box>

      {personsData.length === 0 ? (
        <Box sx={{ px: 2.5, py: 3 }}>
          <Typography color="text.secondary">No guests added yet.</Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridAutoFlow: "column",
            gridTemplateRows: personsData.length > 2 ? "1fr 1fr" : "auto",
            gridAutoColumns: "240px",
            gap: 1.5,
            px: { xs: 2, sm: 2.5 },
            py: 2,
            overflowX: "auto",
            overflowY: "hidden",
            scrollSnapType: "x mandatory",
            WebkitOverflowScrolling: "touch",
            "&::-webkit-scrollbar": { height: 6 },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "#CBD5E1",
              borderRadius: 999,
            },
          }}
        >
          {personsData.map((person, index) => (
            <Box
              key={index}
              sx={{
                minWidth: 0,
                scrollSnapAlign: "start",
                p: 1.75,
                borderRadius: 2.5,
                border: `1px solid ${VM_Colors.light_gray}`,
                bgcolor: VM_Colors.white1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.25 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    bgcolor: "rgba(30, 58, 138, 0.1)",
                    color: VM_Colors.primary,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    fontSize: 15,
                    flexShrink: 0,
                  }}
                >
                  {getPersonInitial(person)}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#64748B" }}>
                    Guest {index + 1}
                  </Typography>
                  <Typography
                    noWrap
                    sx={{ fontSize: 15, fontWeight: 700, color: "#0F172A", lineHeight: 1.3 }}
                  >
                    {getPersonName(person) || "—"}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <PhoneOutlined sx={{ fontSize: 15, color: "#64748B" }} />
                <Typography noWrap sx={{ fontSize: 13.5, color: "#334155", fontWeight: 500 }}>
                  {getPersonPhone(person) || "—"}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Card>
  );

  const renderAccommodationContent = () => {
    if (isPersonCharge) {
      return renderPersonDetails();
    }

    if (!isConfirmed) {
      return (
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            backgroundColor: VM_Colors.white,
            border: `1px solid ${VM_Colors.light_gray}`,
          }}
        >
          <CardContent sx={{ py: 4 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                px: 2,
              }}
            >
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: "50%",
                  bgcolor: "rgba(52, 101, 127, 0.12)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 1.75,
                }}
              >
                <Hotel sx={{ fontSize: 36, color: VM_Colors.teal }} />
              </Box>
              <Typography
                sx={{
                  color: VM_Colors.primary,
                  fontWeight: 600,
                  fontSize: { xs: 14, sm: 15.5 },
                  lineHeight: 1.45,
                  maxWidth: 340,
                }}
              >
                Update lead to confirmed customer to allocate rooms
              </Typography>
            </Box>
          </CardContent>
        </Card>
      );
    }

    return (
      <>
        {!isEmpty(eventData?.accomodation_no_of_rooms) && (
          <Card elevation={1} sx={{ borderRadius: 3, backgroundColor: VM_Colors.white1, mb: 2 }}>
            <CardContent>
              <FormFieldValues
                title="Number of Rooms"
                subtitle={eventData.accomodation_no_of_rooms}
              />
            </CardContent>
          </Card>
        )}
        {!readOnly && (
          <VMAllocateRooms
            initialCustomer={customer}
            initialEvents={
              eventData
                ? (Array.isArray(eventData) ? eventData : [eventData])
                : []
            }
            initialDisplayHeading={false}
            intitialTitleWithBackButtonBar={false}
            invoiceLocked={hasInvoice}
          />
        )}
      </>
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", 
    gap: 2, mt: 4 
    }}>
      {/* Basic Details */}
      <SectionHeader title="Event Details" />
      <Paper
        sx={{
          backgroundColor: VM_Colors.primary,
          color: VM_Colors.white,
          p: 2,
          borderRadius: 3,
        }}
        elevation={3}
      >
        <Grid container 
        alignItems="center"
        justifyContent="space-between"
        // rowSpacing={3}
        >
          <Grid item xs={12} sm={6} md={4}>
            <FormFieldValues
              title="Event Date"
              subtitle={returnDate(eventData.event_date)}
              textColor={VM_Colors.white}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormFieldValues
              title="Event Type"
              subtitle={eventData.event_type}
              textColor={VM_Colors.white}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormFieldValues
              title="Event Name"
              subtitle={eventData.event_name}
              textColor={VM_Colors.white}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Venue Details */}
      <SectionHeader title="Venue Details" />
      <Card elevation={2} sx={{ borderRadius: 3 ,backgroundColor: VM_Colors.white1}}>
        <CardContent>
          <Grid container spacing={4}>
            <Grid item xs={12} sm={6} md={4}>
              <FormFieldValues
                title="Sub Venue Name"
                subtitle={eventData?.event_sub_venue?.sub_venue_name}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormFieldValues
                title="Booking For"
                subtitle={
                  Array.isArray(eventData.event_booking_for)
                    ? eventData.event_booking_for.join(", ")
                    : eventData.event_booking_for
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormFieldValues
                title="No. Of Guests"
                subtitle={eventData.event_no_of_guests}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormFieldValues
                title="Check In"
                subtitle={displayFormattedDateTime(eventData.event_check_in, currencySymbol, { wallClock: true })}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormFieldValues
                title="Check Out"
                subtitle={displayFormattedDateTime(eventData.event_check_out, currencySymbol, { wallClock: true })}
              />
            </Grid>
             <Grid item xs={12} sm={6} md={4}>
              <FormFieldValues
                title="Rent Charges"
                subtitle={formatAmount(eventData.event_rent_charges,currencySymbol)}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Dining Details */}
      <SectionHeader title="Dining Details" />
      {diningDetails.length > 0 ? (
        diningDetails.map((dining, index) => (
          <Card
            key={index}
            elevation={1}
            sx={{
              mb: 2,
              borderRadius: 3,
              backgroundColor: VM_Colors.white1
            //   "&:hover": { boxShadow: 4, transform: "scale(1.01)" },
            //   transition: "0.2s",
            }}
          >
            <CardContent>
              <Typography
                variant="subtitle1"
                fontWeight="500"
                color="text.primary"
                gutterBottom
              >
                {dining.menu}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={4}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormFieldValues title="Menu" subtitle={dining.menu} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormFieldValues
                    title="No. Of Plates"
                    subtitle={dining.no_of_plates}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormFieldValues
                    title="Cost Per Plate"
                    subtitle={`${isEmpty(dining.cost_per_plate ) ? 'NA' : formatAmount(dining.cost_per_plate,currencySymbol)}`}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormFieldValues
                    title="Total Menu Cost"
                    subtitle={`${ formatAmount(dining.total_menu_cost,currencySymbol)}`}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        ))
      ) : (
        <Card elevation={1} sx={{ borderRadius: 3,backgroundColor: VM_Colors.white1 }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              No dining details available
            </Typography>
          </CardContent>
        </Card>
      )}

      <SectionHeader title={isPersonCharge ? "Person Details" : "Accommodation"} />
      {renderAccommodationContent()}

      {/* Additional Services */}
      {eventData?.service_details?.length > 0 && (
        <>
          <SectionHeader title="Additional Services" />
          <Card elevation={2} sx={{ borderRadius: 3,backgroundColor: VM_Colors.white1 }}>
            <CardContent>
              <Grid container spacing={4}>
                {eventData.service_details.map((service, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <FormFieldValues
                      title={service.service_name}
                      subtitle={`${  formatAmount(service.service_charge,currencySymbol) }`}
                    />
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </>
      )}

    </Box>
  );
}

export default VMEDEventInfo;
